<template>
    <div>
        <div v-if="member && ! loading">
            <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

            <div class="row d-print-none">
                <div class="col text-right mb-2">
                    <b-button type="button" v-b-modal.memberNote><i class="fal fa-fw fa-sticky-note"></i> Add/Edit Note</b-button>
                    <b-button :href="$routes.route('member.edit', {member: member.id})"><i class="far fa-fw fa-edit"></i> Edit</b-button>
                    <b-button class="mr-sm-1 mr-md-1" type="button" @click="printPage"><i class="fal fa-fw fa-print"></i> Print</b-button>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1 mb-md-0 mb-lg-2">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="Contact Details"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Name</h6>
                            <h5><template v-if="member.prefix">{{ member.prefix }} </template>{{ member.forename.capitalize() }} {{ member.surname.capitalize() }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>DOB (age)</h6>
                            <h5>{{ dob }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Phone</h6>
                            <h5>{{ member.phone || 'Unknown' }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Email Address<template v-if="member.email_verified_at"> (Verified)</template> <b-button class="d-print-none" @click="resendVerification" :disabled="sending" size="sm" style="font-size: 0.6rem;"><i class="fal fa-fw fa-inbox-out"></i> Resend</b-button></h6>
                            <h5><template v-if="member.email">{{ member.email.toLowerCase() || 'Unknown' }}</template><template v-else>Unknown</template></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Address</h6>
                            <h5 v-if="member.address_1">{{ member.address_1 }}</h5>
                            <h5 v-if="member.address_2">{{ member.address_2 }}</h5>
                            <h5 v-if="member.city">{{ member.city }}</h5>
                            <h5 v-if="member.region">{{ member.region }}</h5>
                            <h5 v-if="member.postcode">{{ member.postcode }}</h5>
                        </b-card-text>
                    </b-card>
                </div>
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="ICE (Incase of Emergencies)"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Name</h6>
                            <h5>{{ member.ice_name || 'Unknown' }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Number</h6>
                            <h5>{{ member.ice_number || 'Unknown' }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Relation</h6>
                            <h5>{{ member.ice_relation || 'Unknown' }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Medical Conditions/Allergies</h6>
                            <h5 v-html="(member.ice_notes && member.ice_notes.length) ? member.ice_notes.nl2br() : 'N/A'"></h5>
                        </b-card-text>
                    </b-card>
                </div>
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="Membership Status"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Membership Level</h6>
                            <h5>{{ member.member_level.title }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Membership Number</h6>
                            <h5>{{ member.member_number }}</h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>UKARA Registered</h6>
                            <h5 v-if="member.ukara"><i class="fas fa-fw fa-check text-success"></i> Yes</h5>
                            <h5 v-else><i class="fas fa-fw fa-times text-danger"></i> No</h5>
                        </b-card-text>
                        <b-card-text v-if="member.ukara" text-tag="div" class="mb-3">
                            <h6>UKARA Expiry</h6>
                            <h5>{{ ukaraExpiry }}</h5>
                        </b-card-text>
                        <b-card-text v-if="$hasRole('Admin') || $hasRole('SuperAdmin')" text-tag="div" class="mb-3 d-print-none">
                            <h6>TOS Accepted</h6>
                            <h5>{{ formatDate(member.agreement_accepted_at) || 'Not Accepted' }}</h5>
                        </b-card-text>
                    </b-card>
                </div>
            </div>

            <div class="row mb-2 d-print-none">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title title="Game History"></b-card-title>
                    <b-card-text>
                        <small>(Booked: {{ counts.booked }} / Show: {{ counts.show }} / No Show: {{ counts.noShow }} / Total: {{ counts.total }})</small>
                    </b-card-text>
                    <template v-if="futureGames.length">
                        <b-card-text>Future Games</b-card-text>
                        <b-card-text text-tag="div">
                            <div id="future-games"></div>
                        </b-card-text>
                    </template>
                    <template v-if="pastGames.length">
                        <b-card-text>Past Games<template v-if="member.noShow"> <i class="text-primary cursor-pointer far fa-fw fa-calendar-exclamation" title="No Show Limit Restricted"></i></template></b-card-text>
                        <b-card-text text-tag="div">
                            <div id="past-games"></div>
                        </b-card-text>
                    </template>
                </b-card>
            </div>

            <div class="row mb-2 d-print-none">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title title="Member Status"></b-card-title>
                    <b-card-text text-tag="div" class="row">
                        <b-button v-if="member.blocked && !member.warning && !member.banned" variant="primary" class="col mx-2" @click="statusChange('blocked')">Blocked</b-button>
                        <b-button v-else variant="outline-primary" class="col mx-2" @click="statusChange('blocked')">Blocked</b-button>
                        <b-button v-if="member.warning && !member.banned" variant="warning" class="col mx-2" @click="statusChange('warning')">Warning</b-button>
                        <b-button v-else variant="outline-warning" class="col mx-2" @click="statusChange('warning')">Warning</b-button>
                        <b-button v-if="member.banned" variant="danger" class="col mx-2" @click="statusChange('banned')">Banned</b-button>
                        <b-button v-else variant="outline-danger" class="col mx-2" @click="statusChange('banned')">Banned</b-button>
                    </b-card-text>
                </b-card>
            </div>

            <div v-if="notes.general.length || notes.marshal.length" class="row mb-2 d-print-none">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title title="Notes"></b-card-title>
                    <b-card-text v-if="notes.general.length" text-tag="h6">General Notes</b-card-text>
                    <hr />
                    <b-card-text v-if="notes.general.length">{{ notes.general[0].message }}</b-card-text>
                    <b-card-text><small>Created {{ notes.general[0].user.name }} at {{ formatDate(notes.general[0].created_at) }} <template v-if="notes.general[0].updated_at && notes.general[0].edit && notes.general[0].edit.name">(Edit {{ notes.general[0].edit.name }} at {{ formatDate(notes.general[0].updated_at) }})</template></small></b-card-text>
                    <hr v-if="notes.general && notes.marshal.length" class="my-4" />
                    <b-card-text v-if="notes.marshal.length" class="mt-0" text-tag="h6">Marshal Notes</b-card-text>
                    <b-card-text v-if="notes.marshal.length" text-tag="div">
                        <div id="notes-table"></div>
                    </b-card-text>
                </b-card>
            </div>

            <div class="row mb-2 d-print-none">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title>
                        <div class="row">
                            <div class="col">
                                <h4>Waivers</h4>
                            </div>
                            <div class="col-md-8 col-sm-8 text-right">
                                <b-button size="sm" @click="generateWaiver()"><i class="fas fa-fw fa-download"></i> Download Waiver</b-button>
                                <b-button size="sm" @click="uploadWaiverModal = true"><i class="fas fa-fw fa-upload"></i> Upload Waiver</b-button>
                            </div>
                        </div>
                    </b-card-title>

                    <div class="row justify-content-center d-print-none">
                        <b-col class="col-12 p-0 m-0">
                            <div v-for="waiver of mostRecentWaiver" :key="'recent-' + waiver.id" class="col-lg-3 col-md-5 col-sm-5 col-xs-12 mx-2 mb-3">
                                <b-card title-tag="h5" :title="'Most Recent ' + waiver.site.title + ' Waiver'" @click="downloadWaiver(waiver)" style="cursor: pointer;">
                                    <b-card-img :src="getWaiverImage(waiver) + '?' + waiver.id"></b-card-img>
                                    <b-card-text text-tag="div" class="pt-2">
                                        <h6>Location</h6>
                                        <h5>{{ waiver.site.title }}</h5>
                                        <h6>Expires</h6>
                                        <h5>{{ getExpiredDate(waiver.expiry) }}</h5>
                                    </b-card-text>
                                </b-card>
                            </div>
                        </b-col>
                        
                        <b-row>
                            <div v-for="waiver of waivers" :key="waiver.id" class="col-lg-3 col-md-5 col-sm-5 col-xs-12 mx-2 mb-3">
                                <b-card @click="downloadWaiver(waiver)" style="cursor: pointer;">
                                    <b-card-img :src="getWaiverImage(waiver) + '?' + waiver.id"></b-card-img>
                                    <b-card-text text-tag="div" class="pt-2">
                                        <h6>Location</h6>
                                        <h5>{{ waiver.site.title }}</h5>
                                        <h6>Expires</h6>
                                        <h5>{{ getExpiredDate(waiver.expiry) }}</h5>
                                    </b-card-text>
                                </b-card>
                            </div>
                        </b-row>
                        
                    </div>
                </b-card>
            </div>

            <b-modal v-model="showNote" size="xl" hide-footer title="Marshal Note">
                <template v-if="chosenNote">
                    <h6><u>Marshal</u></h6>
                    <h5>{{ chosenNote.user.name }}</h5>
                    <h6 class="mt-3"><u>Location</u></h6>
                    <h5>{{ chosenNote.booking.game.site.title }}</h5>
                    <h6 class="mt-3"><u>Game Date</u></h6>
                    <h5 class="mb-4">{{ getGameDate(chosenNote.booking.game.start_time) }}</h5>
                    <hr />
                    <h6 class="mt-4"><u>Note</u></h6>
                    <h5 class="mb-4">{{ chosenNote.message }}</h5>
                    <hr />
                    <b-button class="mt-3" variant="primary" @click="showNote = false; chosenNote = false;">Ok</b-button>
                </template>
            </b-modal>

            <b-modal v-model="uploadWaiverModal" size="xl" hide-footer title="Upload Waiver">
                <b-form @submit="uploadWaiverProcess($event)" @reset="clearWaiverForm()" enctype="multipart/form-data">
                    <b-form-group label="Location" label-for="location">
                        <b-form-select id="location" v-model="uploadWaiver.location" :options="locationOptions"></b-form-select>
                    </b-form-group>
                    <b-form-group label="Expiry" label-for="expiry">
                        <b-form-input type="date" id="expiry" v-model="uploadWaiver.expiry"></b-form-input>
                    </b-form-group>
                    <b-form-group label="Upload File" label-for="upload">
                        <b-form-file id="upload" v-model="uploadWaiver.waiverFile" accept="image/*,application/pdf" :state="Boolean(uploadWaiver.waiverFile)" :placeholder="uploadWaiver.waiverFile ? uploadWaiver.waiverFile.name :'Choose a file or drop it here...'" drop-placeholder="Drop file here..."></b-form-file>
                    </b-form-group>

                    <div class="row">
                        <div class="col-10">
                            <b-progress v-if="uploadProgress" striped animated :value="uploadProgress"></b-progress>
                        </div>
                        <div class="col-2 text-right">
                            <b-button type="reset">Cancel</b-button>
                            <b-button type="submit" variant="primary">Upload</b-button>
                        </div>
                    </div>
                </b-form>
            </b-modal>

            <b-modal id="memberNote" title="Member Note" centered size="xl">
                <b-form>
                    <b-form-textarea v-model="memberNote.message" class="mb-2" rows="3" placeholder="Enter a note for this game"></b-form-textarea>
                    <b-button @click="saveNote" type="button" variant="primary">Save</b-button>
                </b-form>
                <template #modal-footer="{  }"><div></div></template>
            </b-modal>
        </div>
        <div v-else>
            <b-skeleton height="2em" width="100%"></b-skeleton>

            <div class="row">
                <div class="col text-right mb-2">
                    <b-skeleton type="button" width="150px" class="d-inline-block"></b-skeleton>
                    <b-skeleton type="button" width="150px" class="d-inline-block"></b-skeleton>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1 mb-md-0 mb-lg-2">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="Contact Details"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Name</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>DOB (age)</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Phone</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Email Address</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Address</h6>
                            <b-skeleton></b-skeleton>
                            <b-skeleton></b-skeleton>
                            <b-skeleton></b-skeleton>
                            <b-skeleton></b-skeleton>
                            <b-skeleton></b-skeleton>
                        </b-card-text>
                    </b-card>
                </div>
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="ICE (Incase of Emergencies)"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Name</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Number</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>ICE Relation</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Medical Conditions/Allergies</h6>
                            <h5><b-skeleton height="3em"></b-skeleton></h5>
                        </b-card-text>
                    </b-card>
                </div>
                <div class="col-lg col-md-12 mx-sm-1 ml-lg-2 mr-lg-1">
                    <b-card class="mb-md-2 mb-lg-0">
                        <b-card-title title-tag="h5" title="Membership Status"></b-card-title>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Membership Level</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>Membership Number</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>UKARA Registered</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text v-if="member.ukara" text-tag="div" class="mb-3">
                            <h6>UKARA Expiry</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                        <b-card-text text-tag="div" class="mb-3">
                            <h6>TOS Accepted</h6>
                            <h5><b-skeleton></b-skeleton></h5>
                        </b-card-text>
                    </b-card>
                </div>
            </div>

            <div class="row mb-2">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title title="Game History"></b-card-title>
                    <b-card-text>Future Games</b-card-text>
                    <b-card-text text-tag="div">
                        <b-skeleton-table :rows="4" :columns="3"></b-skeleton-table>
                    </b-card-text>
                    <b-card-text>Past Games</b-card-text>
                    <b-card-text text-tag="div">
                        <b-skeleton-table :rows="4" :columns="3"></b-skeleton-table>
                    </b-card-text>
                </b-card>
            </div>

            <div class="row mb-2">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title title="Member Status"></b-card-title>
                    <b-card-text text-tag="div" class="row">
                        <b-skeleton type="button"></b-skeleton>
                        <b-skeleton type="button"></b-skeleton>
                        <b-skeleton type="button"></b-skeleton>
                    </b-card-text>
                </b-card>
            </div>

            <div class="row mb-2">
                <b-card class="col mx-sm-1 mx-md-4">
                    <b-card-title>
                        <div class="row">
                            <div class="col">
                                <h4>Waivers</h4>
                            </div>
                            <div class="col-md-8 col-sm-8 text-right">
                                <b-skeleton type="button" width="150px" class="d-inline-block"></b-skeleton>
                                <b-skeleton type="button" width="150px" class="d-inline-block"></b-skeleton>
                            </div>
                        </div>
                    </b-card-title>

                    <div class="row justify-content-center">
                        <b-col class="col-12 p-0 m-0">
                            <div class="col-lg-3 col-md-5 col-sm-5 col-xs-12 mx-2 mb-3">
                                <b-skeleton height="200px" width="33%"></b-skeleton>
                            </div>
                        </b-col>
                        
                    </div>
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment-timezone';

export default {
    props: {
        member_id: {
            default: false,
        },
        memberRaw: {
            default: false,
        },
    },
    data() {
        return {
            breadcrumbItems: [],
            member: false,
            gameCount: false,
            dob: "unknown",
            now: new moment(),
            sites: [],
            memberExpiry: "Unknown",
            ukaraExpiry: "Unknown",
            pastGamesTable: false,
            futureGamesTable: false,
            notes: {
                general: [],
                marshal: [],
            },
            memberNote: {
                message: null,
            },
            notesTable: false,
            chosenNote: false,
            showNote: false,
            mostRecentWaiver: {},
            waivers: [],
            locationOptions: [
                {
                    value: null,
                    text: "Select Location",
                },
            ],
            uploadWaiver: {
                location: null,
                expiry: new moment().add('1', 'years').format('YYYY-MM-DD'),
                waiverFile: null,
            },
            uploadWaiverModal: false,
            uploadProgress: false,
            loading: true,
            sending: false,
            sendingSuccess: null,
            counts: {
                'booked': 0,
                'show': 0,
                'noShow': 0,
                'total': 0,
            },
        }
    },
    computed: {
        pastGames() {
            let past = [];

            if (this.member.bookings) {
                past = this.member.bookings.filter((game) => {
                    return new moment(game.game.start_time) < this.now;
                });

                past.forEach((game) => {
                    game.show = (game.state == 'arrived') ? true : false;
                });
            }

            return past;
        },
        futureGames() {
            let future = [];

            if (this.member.bookings) {
                future = this.member.bookings.filter((game) => {
                    return new moment(game.game.start_time) > this.now;
                });
            }

            return future;
        },
    },
    methods: {
        printPage() {
            window.print();
        },
        formatDate(dateString) {
            // Convert the date string into local timezone and format it to DD/MM/YYYY HH:MM
            return dateString ? new moment(dateString).format('DD/MM/YYYY HH:mm') : null;
        },
        saveNote() {
            if (this.memberNote.id) {
                axios.put(this.$routes.route('note.update', {note: this.memberNote.id}), this.memberNote)
                    .then((response) => {
                        this.getMemberData();
                        this.$bvModal.hide('memberNote');
                        this.$bvToast.toast('Note updated', {
                            title: 'Success',
                            variant: 'success',
                            solid: true,
                            autoHideDelay: 5000,
                        });
                    })
                    .catch((err) => {
                        console.error(err);
                    })
            } else {
                axios.post(this.$routes.route('note.storeForMember', {member: this.member.id}), this.memberNote)
                    .then((response) => {
                        this.getMemberData();
                        this.$bvModal.hide('memberNote');
                        this.$bvToast.toast('Note saved', {
                            title: 'Success',
                            variant: 'success',
                            solid: true,
                            autoHideDelay: 5000,
                        });
                    })
                    .catch((err) => {
                        console.error(err);
                    })
            }
        },
        generateWaiver() {
            window.open(this.$routes.route('waiver.generate', {member: this.member_id}), '_blank');
        },
        resendVerification() {
            this.sending = true;
            axios.get(this.$routes.route('member.resendVerification', {member: this.member.id}))
                .then((response) => {
                    this.sendingSuccess = true;
                    this.sending = false;
                    this.$bvToast.toast('Verification email sent', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 5000,
                    });
                })
                .catch((err) => {
                    console.error(err);
                })
        },
        uploadWaiverProcess(e) {
            e.preventDefault();

            var data = new FormData();
            data.append("site_id", this.uploadWaiver.location);
            data.append("member_id", this.member.id);
            data.append("expiry", this.uploadWaiver.expiry);
            data.append("waiverFile", this.uploadWaiver.waiverFile);

            axios.post(this.$routes.route('waiver.store'), data, {
                onUploadProgress: (progressEvent) => {
                    this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    console.log(this.uploadProgress)
                }
            })
                .then((result) => {
                    this.clearWaiverForm();
                    this.getMemberData();
                })
                .catch((err) => {
                    console.error(err);
                })
        },
        clearWaiverForm() {
            this.uploadWaiver.location = null;
            this.uploadWaiver.expiry = new moment().add('1', 'years').format('YYYY-MM-DD') + ' 00:00:00';
            this.uploadWaiver.waiverFile = null;
            this.uploadWaiverModal = false;
            this.uploadProgress = false;
        },
        downloadWaiver(waiver) {
            console.log("Download waiver:", waiver.id);
            var url = `/waiver/${waiver.id}/download`;
            window.open(this.$routes.route('waiver.download', {waiver: waiver.id}), '_blank');
        },
        getWaiverImage(waiver) {
            if ((/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i).test(waiver.filename)) {
                return `/storage/waiver/${waiver.site_id}/${waiver.member_id}/${waiver.filename}`;
            } else {
                return `/images/pdf-placeholder.jpg`;
            }
        },
        getGameDate(dateString) {
            return new moment(dateString).format('DD/MM/YYYY HH:mm')
        },
        getExpiredDate(dateString) {
            return new moment(dateString).format('DD/MM/YYYY')
        },
        statusChange(action) {
            if (action == 'blocked' && ! this.member.blocked) {
                if (this.member.warning) {
                    this.processStatusChange('warning');
                }
                if (this.member.banned) {
                    this.processStatusChange('banned');
                }
            } else if (action == 'warning' && ! this.member.warning) {
                if (this.member.blocked) {
                    this.processStatusChange('blocked');
                }
                if (this.member.banned) {
                    this.processStatusChange('banned');
                }
            } else if (action == 'banned' && ! this.member.banned) {
                if (this.member.blocked) {
                    this.processStatusChange('blocked');
                }
                if (this.member.warning) {
                    this.processStatusChange('warning');
                }
            }

            this.processStatusChange(action);
        },
        processStatusChange(action) {
            var value = !this.member[action];
            value = value ? "true" : "false";

            axios.post(this.$routes.route('member.statusChange', {member: this.member.id, action: action, status: value}))
                .then((response) => {
                    console.log(response.data);
                    this.member[action] = (value == 'true') ? 1 : 0;
                })
                .catch((err) => {
                    console.error(err);
                })
        },
        getMemberData() {
            axios.get('/member/' + this.member_id + '?getData=true')
            .then((result) => {

                axios.get(this.$routes.route('member.gameCount', {member:this.member_id}))
                    .then((response) => {
                        this.counts = response.data;
                    })
                    .catch((err) => {
                        console.error(err);
                    });

                // console.log(this.notes);
                this.member = result.data;
                if (this.member.notes && this.member.notes.length) {
                    this.memberNote = this.member.notes[0];
                }

                this.loading = false;
                this.gameCount = result.data.bookings.length;
                // If notes key is not present or empty, set it to an empty array else set it to the notes array
                this.notes.general = (this.member.notes) ? this.member.notes : [];
                this.member.bookings.forEach((booking) => {
                    if (booking.notes && booking.notes.length) {
                        booking.notes.forEach((note) => {
                            var exists = this.notes.marshal.find((n) => {
                                return n.id == note.id;
                            });

                            if ( ! exists) {
                                note.booking = booking;
                                this.notes.marshal.push(note);
                            }
                        });
                    }
                });
                // this.notes.marshal = this.member.marshal_notes;

                if (this.member.dob) {
                    var diff = this.now.diff(moment(this.member.dob), "years");
                    this.dob = new moment(this.member.dob).format("DD/MM/YYYY") + " (" + diff + ")";
                } else {
                    this.dob = "Unknown";
                }

                if (this.member.member_expiry) {
                    this.memberExpiry = new moment(this.member.member_expiry).format("DD/MM/YYYY");
                }

                if (this.member.ukara_expiry) {
                    this.ukaraExpiry = new moment(this.member.ukara_expiry).format("DD/MM/YYYY");
                }

                this.breadcrumbItems = [
                    {
                        text: "Members List",
                        href: "/member",
                    },
                    {
                        text: `${this.member.forename.toUpperCase()} ${this.member.surname.toUpperCase()}`,
                        active: true,
                    }
                ];

                // this.waivers = this.member.waivers.filter((waiver) => {
                //     return this.sites.general[waiver.location];
                // })
                this.waivers = this.member.waivers;

                // Reorder the waivers based on the field 'expiry' DESC
                this.waivers.sort((a, b) => {
                    return new moment(b.expiry) - new moment(a.expiry);
                });

                this.mostRecentWaiver = {};

                this.member.waivers.forEach((waiver) => {
                    if (!this.mostRecentWaiver[waiver.site.id]) {
                        this.mostRecentWaiver[waiver.site.id] = waiver;
                    } else {
                        if (this.mostRecentWaiver[waiver.site.id].expiry < waiver.expiry) {
                            this.mostRecentWaiver[waiver.site.id] = waiver;
                        }
                    }
                });

                setTimeout(() => {
                    if (document.getElementById('past-games')) {
                        this.pastGamesTable = new Tabulator(
                            "#past-games",
                            {
                                pagination: "local",
                                paginationSize: 5,
                                initialSort: [{ column: "game.start_time", dir: "desc" }],
                                data: this.pastGames,
                                layout: "fitColumns",
                                columns: [
                                    {
                                        title: "Location",
                                        field: "game.site.title",
                                        formatter: (cell) => {
                                            var data = cell.getValue();
                                            return data;
                                        }
                                    },
                                    {
                                        title: "Date",
                                        field: "game.start_time",
                                        sorter: "date",
                                        sorterParams: { format: "YYYY/MM/DD HH:mm:ss" },
                                        formatter: function(cell) {
                                            var playDate = new moment(cell.getValue());

                                            return playDate.format("DD/MM/YYYY");
                                        }
                                    },
                                    {
                                        title: "Time",
                                        field: "game.start_time",
                                        sorter: "date",
                                        sorterParams: { format: "YYYY/MM/DD HH:mm:ss" },
                                        formatter: function(cell) {
                                            var playDate = new moment(cell.getValue());

                                            return playDate.format("HH:mm");
                                        }
                                    },
                                    {
                                        title: "Show",
                                        field: "show",
                                        formatter: "tickCross",
                                        width: 100,
                                        align: "center",
                                    },
                                ],
                                rowClick: (e, row) => {
                                    let data = row.getData();

                                    window.open(this.$routes.route('game.show', {game: data.game.id}), '_blank');
                                }
                            }
                        );
                    }
                    if (document.getElementById('future-games')) {
                        this.futureGamesTable = new Tabulator(
                            "#future-games",
                            {
                                pagination: "local",
                                paginationSize: 5,
                                initialSort: [{ column: "game.start_time", dir: "desc" }],
                                data: this.futureGames,
                                layout: "fitColumns",
                                columns: [
                                    {
                                        title: "Location",
                                        field: "game.site.title",
                                        formatter: (cell) => {
                                            var data = cell.getValue();
                                            // console.log(data);
                                            // return game.site.title;
                                            return data;
                                        }
                                    },
                                    {
                                        title: "Date",
                                        field: "game.start_time",
                                        sorter: "date",
                                        sorterParams: { format: "YYYY/MM/DD HH:mm:ss" },
                                        formatter: function(cell) {
                                            var playDate = new moment(cell.getValue());

                                            return playDate.format("DD/MM/YYYY");
                                        }
                                    },
                                    {
                                        title: "Time",
                                        field: "game.start_time",
                                        sorter: "date",
                                        sorterParams: { format: "YYYY/MM/DD HH:mm:ss" },
                                        formatter: function(cell) {
                                            var playDate = new moment(cell.getValue());

                                            return playDate.format("HH:mm");
                                        }
                                    },
                                ],
                                rowClick: (e, row) => {
                                    let data = row.getData();

                                    window.open(this.$routes.route('game.show', {game: data.game.id}), '_blank');
                                }
                            }
                        );
                    }
                    if (document.getElementById('notes-table')) {
                        this.notesTable = new Tabulator(
                            "#notes-table",
                            {
                                pagination: "local",
                                paginationSize: 5,
                                initialSort: [
                                    { column: "created_at", dir: "desc" },
                                    { column: "booking.game.start_time", dir: "desc" }
                                ],
                                data: this.notes.marshal,
                                layout: "fitColumns",
                                columns: [
                                    {
                                        title: "Location",
                                        field: "booking.game.site.title",
                                    },
                                    {
                                        title: "Date",
                                        field: "booking.game.start_time",
                                        sorter: "date",
                                        sorterParams: { format: "YYYY/MM/DD HH:mm:ss" },
                                        formatter: function(cell) {
                                            var playDate = new moment(cell.getValue());

                                            return playDate.format("DD/MM/YYYY HH:mm");
                                        }
                                    },
                                    {
                                        title: "Marshal",
                                        field: "user.name",
                                    },
                                    {
                                        title: "Note",
                                        field: "message",
                                        formatter: (cell) => {
                                            var data = cell.getValue();
                                            let maxLength = 25;
                                            if (data.length > maxLength) {
                                                data = data.substr(0, maxLength) + '...';
                                            }
                                            return data;
                                        }
                                    },
                                ],
                                rowClick: (e, row) => {
                                    this.chosenNote = row.getData();
                                    this.showNote = true;
                                }
                            }
                        );
                    }
                }, 100);
            })
            .catch((err) => {
                console.error(err);
            })
        },
    },
    mounted() {
        this.member = this.memberRaw;
        setTimeout(() => {
            axios.get(this.$routes.route('site.index'))
            .then((result) => {
                this.sites = result.data;

                for (var site of this.sites) {
                    this.locationOptions.push({
                        value: site.id,
                        text: site.title,
                    })
                }
                this.getMemberData();
            })
            .catch((err) => {
                console.error(err);
            });
        }, 100);

        setInterval(() => {
            this.getMemberData();
        }, 30000);
    },
}
</script>